import { CORE_API_HOSTNAME } from "../../env";
import { getDecodedJwt, validToken } from "../../helpers/jwt";
import { refresh } from "../auth";
import { fetchExchangeToken } from "../dinteroAuthApi";
import type { PartnerUser } from "../types";
import { getAccountIdFromAudience } from "./helpers";

export interface ExchangeRefreshResult {
    /** A freshly minted exchange access token for the inspected account. */
    accessToken: string;
    /**
     * Present only when the partner cognito session had to be refreshed, so the
     * caller can persist the updated partner session.
     */
    partnerSession?: PartnerUser;
}

/**
 * Determine whether `token` is an exchange token minted for an inspect session.
 * Requires a stored partner session, since only a partner can refresh it.
 */
export const isExchangeToken = (
    token: string | undefined,
    partnerSession: PartnerUser | undefined,
): boolean => {
    if (!token || !partnerSession) {
        return false;
    }
    const { iss, exchanged, aud } = getDecodedJwt(token);
    return Boolean(aud && iss === CORE_API_HOSTNAME && exchanged);
};

/**
 * Re-mint an (expired) exchange token using the partner session it was derived
 * from. Refreshes the partner cognito token first when necessary.
 *
 * Returns `undefined` when the partner session cannot be refreshed or the
 * exchange request fails. Callers must NOT fall back to refreshing the expired
 * exchange token as if it were a regular cognito session — its refresh token
 * and config belong to the partner, not the inspected account.
 */
export const refreshExchangeToken = async (
    expiredExchangeToken: string,
    partnerSession: PartnerUser,
): Promise<ExchangeRefreshResult | undefined> => {
    const { exchanged, aud } = getDecodedJwt(expiredExchangeToken);
    if (!exchanged || !aud) {
        return undefined;
    }

    let partnerToken = validToken(partnerSession.cognito_access_token);
    let refreshedPartnerSession: PartnerUser | undefined;
    if (!partnerToken) {
        const updated = await refresh(partnerSession);
        if (updated) {
            refreshedPartnerSession = { ...partnerSession, ...updated };
            partnerToken = updated.cognito_access_token;
        }
    }
    if (!partnerToken) {
        return undefined;
    }

    // exchanged.account_id is the partner (URL) account; the `aud` username is
    // the inspected account — mirrors the original inspect exchange request.
    const inspectedAccountId = getAccountIdFromAudience(aud);
    const exchangeToken = await fetchExchangeToken(
        exchanged.account_id,
        inspectedAccountId,
        partnerToken,
    );
    if (!exchangeToken) {
        return undefined;
    }

    return {
        accessToken: exchangeToken.access_token,
        partnerSession: refreshedPartnerSession,
    };
};
